import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  QrCodeIcon, 
  MessageCircle, 
  User, 
  Mail, 
  MapPin, 
  ArrowRight, 
  ArrowLeft,
  Play,
  X,
  CheckCircle,
  Smartphone,
  Shield
} from 'lucide-react';
import QRCode from 'react-qr-code';

export default function DemoModal({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  const demoSteps = [
    {
      title: "Step 1: Create Your QR Code",
      subtitle: "Set up anonymous communication",
      content: (
        <div className="text-center space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create QR Code for "My Wallet"</h3>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <QRCode value="https://icomment.app/DEMO1" size={120} className="mx-auto" />
              <p className="text-sm text-gray-600 mt-2">QR Code: DEMO1</p>
            </div>
          </div>
          <div className="text-left bg-gray-50 p-4 rounded-lg">
            <p className="font-medium text-gray-900">Greeting Message:</p>
            <p className="text-gray-700 italic">"Hi! You found my wallet. Please let me know where you found it and I can arrange to pick it up. Thank you!"</p>
          </div>
        </div>
      )
    },
    {
      title: "Step 2: Someone Finds Your Item",
      subtitle: "Finder scans the QR code",
      content: (
        <div className="text-center space-y-6">
          <div className="flex justify-center items-center space-x-4">
            <div className="bg-gray-100 p-4 rounded-full">
              <Smartphone className="h-8 w-8 text-gray-600" />
            </div>
            <ArrowRight className="h-6 w-6 text-blue-600" />
            <div className="bg-white p-3 rounded-lg shadow-lg border-2 border-blue-200">
              <QRCode value="https://icomment.app/DEMO1" size={80} />
            </div>
          </div>
          
          <Card className="max-w-sm mx-auto">
            <CardHeader className="text-center pb-3">
              <CardTitle className="text-lg">My Wallet</CardTitle>
              <CardDescription>Send a message to the owner</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-blue-50 p-3 rounded text-sm text-gray-700">
                "Hi! You found my wallet. Please let me know where you found it..."
              </div>
              <div className="text-xs text-gray-500">
                <User className="inline h-3 w-3 mr-1" />
                Anonymous finder can send message
              </div>
            </CardContent>
          </Card>
        </div>
      )
    },
    {
      title: "Step 3: Anonymous Message Sent",
      subtitle: "Secure communication without exposing contacts",
      content: (
        <div className="text-center space-y-6">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <MessageCircle className="h-5 w-5 mr-2 text-blue-600" />
                Message from Finder
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg text-left">
                <p className="text-gray-900">"Hi! I found your wallet at Central Library on Main Street. It was on table 15 in the study area. I can meet you there tomorrow at 3 PM or let me know another time. Thanks!"</p>
              </div>
              
              <div className="flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  <span>finder@example.com</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1 text-green-600" />
                  <span>Location shared</span>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="flex items-center justify-center text-green-700">
              <CheckCircle className="h-5 w-5 mr-2" />
              <span className="font-medium">Email notification sent to wallet owner!</span>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Step 4: Owner Replies Safely",
      subtitle: "Two-way communication through the platform",
      content: (
        <div className="text-center space-y-6">
          <Card className="max-w-md mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <User className="h-5 w-5 mr-2 text-green-600" />
                Reply from Owner
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg text-left">
                <p className="text-gray-900">"Thank you so much for finding my wallet! Yes, tomorrow at 3 PM at Central Library works perfectly. I'll be there. Really appreciate your honesty!"</p>
              </div>
              
              <Badge className="bg-blue-100 text-blue-800">
                Reply sent via iComment.app platform
              </Badge>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="bg-blue-50 p-3 rounded-lg">
              <Shield className="h-5 w-5 mx-auto text-blue-600 mb-2" />
              <p className="font-medium">Privacy Protected</p>
              <p className="text-gray-600">No direct contact sharing</p>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <CheckCircle className="h-5 w-5 mx-auto text-green-600 mb-2" />
              <p className="font-medium">Mission Complete</p>
              <p className="text-gray-600">Item safely returned</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Ready to Get Started?",
      subtitle: "Join thousands using iComment.app",
      content: (
        <div className="text-center space-y-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">10,000+</div>
                <div className="text-sm text-gray-600">Items Protected</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">2,500+</div>
                <div className="text-sm text-gray-600">Happy Users</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">95%</div>
                <div className="text-sm text-gray-600">Return Rate</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">24h</div>
                <div className="text-sm text-gray-600">Avg Response</div>
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-4">
              Start protecting your items today!
            </h3>
            
            <div className="space-y-3">
              <Button 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                onClick={() => { onClose(); window.location.href = '/auth'; }}
              >
                Get Started Free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              
              <div className="flex justify-center space-x-4 text-sm text-gray-600">
                <span>✅ No credit card required</span>
                <span>✅ 10 free QR codes</span>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  const nextStep = () => {
    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const resetDemo = () => {
    setCurrentStep(0);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="flex flex-row items-center justify-between">
          <div>
            <DialogTitle className="text-2xl">iComment.app Demo</DialogTitle>
            <p className="text-gray-600">See how anonymous communication works</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogHeader>

        <div className="mt-6">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Step {currentStep + 1} of {demoSteps.length}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentStep + 1) / demoSteps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-600 to-indigo-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Current Step */}
          <div className="mb-8">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {demoSteps[currentStep].title}
              </h2>
              <p className="text-gray-600">
                {demoSteps[currentStep].subtitle}
              </p>
            </div>
            
            <div className="min-h-[400px] flex items-center justify-center">
              {demoSteps[currentStep].content}
            </div>
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center pt-6 border-t">
            <Button
              variant="outline"
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Previous
            </Button>
            
            <div className="flex space-x-2">
              {demoSteps.map((_, index) => (
                <button
                  key={index}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    index === currentStep 
                      ? 'bg-blue-600' 
                      : index < currentStep 
                        ? 'bg-green-600' 
                        : 'bg-gray-300'
                  }`}
                  onClick={() => setCurrentStep(index)}
                />
              ))}
            </div>
            
            {currentStep === demoSteps.length - 1 ? (
              <Button
                onClick={resetDemo}
                className="flex items-center"
              >
                <Play className="mr-2 h-4 w-4" />
                Watch Again
              </Button>
            ) : (
              <Button
                onClick={nextStep}
                className="flex items-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}